import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Copy, ExternalLink, Navigation, X, GripHorizontal } from 'lucide-react';
import toast from 'react-hot-toast';
import type { MapOverlayConfig } from './types';
import { useMediaQuery } from '../../../hooks/core/useMediaQuery';
import { useGeocode } from './useGeocode';
import { useDragResize } from './useDragResize';
import { useBottomSheet } from './useBottomSheet';
import { resolveAddressString, computeInitialPosition, getDirectionsUrl } from './utils';
import { MapOverlayContent } from './MapOverlayContent';

interface MapOverlayPanelProps {
  config: MapOverlayConfig;
  onClose: () => void;
}

const RESIZE_EDGES = ['n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw'] as const;

const resizeEdgeClasses: Record<string, string> = {
  n: 'top-0 left-2 right-2 h-1.5 cursor-ns-resize',
  s: 'bottom-0 left-2 right-2 h-1.5 cursor-ns-resize',
  e: 'right-0 top-2 bottom-2 w-1.5 cursor-ew-resize',
  w: 'left-0 top-2 bottom-2 w-1.5 cursor-ew-resize',
  ne: 'top-0 right-0 w-3 h-3 cursor-nesw-resize',
  nw: 'top-0 left-0 w-3 h-3 cursor-nwse-resize',
  se: 'bottom-0 right-0 w-3 h-3 cursor-nwse-resize',
  sw: 'bottom-0 left-0 w-3 h-3 cursor-nesw-resize',
};

function DesktopPanel({ config, onClose }: MapOverlayPanelProps) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const addressStr = resolveAddressString(config.address);
  const { lat, lng, isLoading, error } = useGeocode(config.address, config.lat, config.lng);

  const panelWidth = Math.min(config.defaultWidth ?? 600, window.innerWidth - 16);
  const panelHeight = Math.min(config.defaultHeight ?? 450, window.innerHeight - 32);
  const triggerRect = config.triggerRef.current?.getBoundingClientRect();
  const initialPos = triggerRect
    ? computeInitialPosition(triggerRect, panelWidth, panelHeight)
    : { x: Math.max(0, (window.innerWidth - panelWidth) / 2), y: 60 };

  const { position, size, dragHandleProps, getResizeHandleProps } = useDragResize({
    initialPosition: initialPos,
    initialSize: { width: panelWidth, height: panelHeight },
    minWidth: Math.min(config.minWidth ?? 400, window.innerWidth - 16),
    minHeight: Math.min(config.minHeight ?? 300, window.innerHeight - 32),
  });

  useEffect(() => {
    closeRef.current?.focus();
  }, []);

  useEffect(() => {
    // Capture phase on window, and stop it there: the map can be opened from inside a
    // ResponsiveOverlay popup (the generator page's Location tile), whose own Escape
    // listener sits on document. Without this, one Escape closed the map AND the popup.
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      onClose();
    };
    window.addEventListener('keydown', handleEscape, true);
    return () => window.removeEventListener('keydown', handleEscape, true);
  }, [onClose]);

  const handleCopyAddress = () => {
    import('../../../lib/utils/clipboard').then(({ safeWriteClipboard }) =>
      safeWriteClipboard(addressStr).then(ok =>
        ok ? toast.success('Address copied') : toast.error('Failed to copy address'),
      ),
    );
  };

  const handleDirections = () => {
    window.open(getDirectionsUrl(addressStr), '_blank', 'noopener');
  };

  const handleOpenMaps = () => {
    const encoded = encodeURIComponent(addressStr);
    window.open(`https://www.google.com/maps/search/?api=1&query=${encoded}`, '_blank', 'noopener');
  };

  return (
    <div
      role="dialog"
      aria-label={config.title ?? 'Map overlay'}
      className="fixed z-[55] flex flex-col bg-white rounded-xl border border-slate-200 shadow-2xl overflow-hidden max-w-[calc(100vw-1rem)] max-h-[calc(100vh-2rem)]"
      style={{
        left: position.x,
        top: position.y,
        width: size.width,
        height: size.height,
      }}
    >
      {/* Resize handles */}
      {RESIZE_EDGES.map((edge) => (
        <div
          key={edge}
          className={`absolute z-20 ${resizeEdgeClasses[edge]}`}
          {...getResizeHandleProps(edge)}
        />
      ))}

      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-300 bg-slate-50 select-none">
        <div className="flex items-center gap-2 flex-1 min-w-0" {...dragHandleProps}>
          <GripHorizontal className="w-4 h-4 text-slate-400 flex-shrink-0" />
          <div className="min-w-0">
            {config.title && (
              <h2 className="text-sm font-semibold text-slate-800 truncate">{config.title}</h2>
            )}
            <p className="text-xs text-slate-500 truncate">{config.subtitle ?? addressStr}</p>
          </div>
        </div>

        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            onClick={handleCopyAddress}
            className="p-1.5 rounded-md hover:bg-slate-200 transition-colors"
            aria-label="Copy address"
          >
            <Copy className="w-3.5 h-3.5 text-slate-500" />
          </button>
          <button
            onClick={handleDirections}
            className="p-1.5 rounded-md hover:bg-slate-200 transition-colors"
            aria-label="Get directions"
          >
            <Navigation className="w-3.5 h-3.5 text-slate-500" />
          </button>
          <button
            onClick={handleOpenMaps}
            className="p-1.5 rounded-md hover:bg-slate-200 transition-colors"
            aria-label="Open in Google Maps"
          >
            <ExternalLink className="w-3.5 h-3.5 text-slate-500" />
          </button>
          <button
            ref={closeRef}
            onClick={onClose}
            className="p-1.5 rounded-md hover:bg-slate-200 transition-colors"
            aria-label="Close map"
          >
            <X className="w-3.5 h-3.5 text-slate-500" />
          </button>
        </div>
      </div>

      {/* Content */}
      <MapOverlayContent
        lat={lat}
        lng={lng}
        isLoading={isLoading}
        error={error}
        zoom={config.defaultZoom ?? 15}
        mapType={config.defaultMapType ?? 'roadmap'}
        markerLabel={config.markerLabel}
        pinPlacement={config.pinPlacement}
        infoStrip={config.infoStrip}
        sidebar={config.sidebar}
        footer={config.footer}
        isMobile={false}
      />
    </div>
  );
}

function MobilePanel({ config, onClose }: MapOverlayPanelProps) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const addressStr = resolveAddressString(config.address);
  const { lat, lng, isLoading, error } = useGeocode(config.address, config.lat, config.lng);

  const { dragHandleProps, sheetStyle } = useBottomSheet({
    onDismiss: onClose,
    defaultHeightPercent: 70,
    maxHeightPercent: 90,
  });

  useEffect(() => {
    closeRef.current?.focus();
  }, []);

  useEffect(() => {
    // Capture phase on window, and stop it there: the map can be opened from inside a
    // ResponsiveOverlay popup (the generator page's Location tile), whose own Escape
    // listener sits on document. Without this, one Escape closed the map AND the popup.
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      onClose();
    };
    window.addEventListener('keydown', handleEscape, true);
    return () => window.removeEventListener('keydown', handleEscape, true);
  }, [onClose]);

  const handleCopyAddress = () => {
    import('../../../lib/utils/clipboard').then(({ safeWriteClipboard }) =>
      safeWriteClipboard(addressStr).then(ok =>
        ok ? toast.success('Address copied') : toast.error('Failed to copy address'),
      ),
    );
  };

  const handleDirections = () => {
    window.open(getDirectionsUrl(addressStr), '_blank', 'noopener');
  };

  const handleOpenMaps = () => {
    const encoded = encodeURIComponent(addressStr);
    window.open(`https://www.google.com/maps/search/?api=1&query=${encoded}`, '_blank', 'noopener');
  };

  return (
    <div
      role="dialog"
      aria-label={config.title ?? 'Map overlay'}
      className="fixed inset-x-0 bottom-0 z-[55] flex flex-col bg-white rounded-t-2xl shadow-2xl overflow-hidden"
      style={sheetStyle}
    >
      {/* Drag handle pill */}
      <div className="flex justify-center pt-2 pb-1" {...dragHandleProps}>
        <div className="w-10 h-1 bg-slate-300 rounded-full" />
      </div>

      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-300">
        <div className="flex-1 min-w-0">
          {config.title && (
            <h2 className="text-sm font-semibold text-slate-800 truncate">{config.title}</h2>
          )}
          <p className="text-xs text-slate-500 truncate">{config.subtitle ?? addressStr}</p>
        </div>

        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            onClick={handleCopyAddress}
            className="p-1.5 rounded-md hover:bg-slate-200 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label="Copy address"
          >
            <Copy className="w-4 h-4 text-slate-500" />
          </button>
          <button
            onClick={handleDirections}
            className="p-1.5 rounded-md hover:bg-slate-200 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label="Get directions"
          >
            <Navigation className="w-4 h-4 text-slate-500" />
          </button>
          <button
            onClick={handleOpenMaps}
            className="p-1.5 rounded-md hover:bg-slate-200 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label="Open in Google Maps"
          >
            <ExternalLink className="w-4 h-4 text-slate-500" />
          </button>
          <button
            ref={closeRef}
            onClick={onClose}
            className="p-1.5 rounded-md hover:bg-slate-200 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label="Close map"
          >
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>
      </div>

      {/* Content */}
      <MapOverlayContent
        lat={lat}
        lng={lng}
        isLoading={isLoading}
        error={error}
        zoom={config.defaultZoom ?? 15}
        mapType={config.defaultMapType ?? 'roadmap'}
        markerLabel={config.markerLabel}
        pinPlacement={config.pinPlacement}
        infoStrip={config.infoStrip}
        sidebar={config.sidebar}
        footer={config.footer}
        isMobile={true}
      />
    </div>
  );
}

export function MapOverlayPanel({ config, onClose }: MapOverlayPanelProps) {
  const isMobile = useMediaQuery('(max-width: 767px)');
  const triggerRef = config.triggerRef;

  const handleClose = () => {
    onClose();
    triggerRef.current?.focus();
  };

  return createPortal(
    isMobile
      ? <MobilePanel config={config} onClose={handleClose} />
      : <DesktopPanel config={config} onClose={handleClose} />,
    document.body,
  );
}
